import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "@redotech/redo-web/arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import {
  formatShortcut,
  getShortcutDisplayParts,
} from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { messageInputShortcuts } from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useCallback, useMemo, useState } from "react";
import type {
  useDeleteDraftAction,
  useSendMessageAction,
} from "../actions/use-conversation-actions";

export const MessageInputActions = memo(function MessageInputActions({
  sendAction,
  deleteDraftAction,
  onSend,
  onSendAndClose,
  onSendAndMarkInProgress,
  onDeleteDraft,
  isDeletingDraft,
  isSavingDraft,
  isSending,
}: {
  sendAction: ReturnType<typeof useSendMessageAction>;
  deleteDraftAction: ReturnType<typeof useDeleteDraftAction>;
  onSend: () => void;
  onSendAndClose: () => void;
  onSendAndMarkInProgress: () => void;
  onDeleteDraft: () => void;
  isDeletingDraft: boolean;
  isSavingDraft: boolean;
  isSending: boolean;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);

  const handleSetButtonRef = useCallback(
    (ref: HTMLButtonElement) => setButtonRef(ref),
    [],
  );

  const menuItems: RedoCommandMenuItem[] = useMemo(
    () => [
      {
        text: "Send",
        shortcutHotkey: getShortcutDisplayParts(
          messageInputShortcuts.SEND_MESSAGE,
        ),
        onClick: () => {
          setDropdownOpen(false);
          onSend();
        },
      },
      {
        text: "Send and mark in progress",
        shortcutHotkey: getShortcutDisplayParts(
          messageInputShortcuts.SEND_AND_MARK_IN_PROGRESS,
        ),
        onClick: () => {
          setDropdownOpen(false);
          onSendAndMarkInProgress();
        },
      },
    ],
    [onSend, onSendAndMarkInProgress],
  );

  return (
    <>
      {deleteDraftAction?.shortcut ? (
        <Tooltip
          title={`${deleteDraftAction.title} (${formatShortcut(deleteDraftAction.shortcut)})`}
        >
          <RedoButton
            hierarchy="tertiary"
            IconLeading={deleteDraftAction.Icon}
            onClick={onDeleteDraft}
            pending={isDeletingDraft || isSavingDraft}
            size="sm"
            text=""
          />
        </Tooltip>
      ) : (
        <RedoButton
          hierarchy="tertiary"
          IconLeading={deleteDraftAction.Icon}
          onClick={onDeleteDraft}
          pending={isDeletingDraft || isSavingDraft}
          size="sm"
          text=""
        />
      )}
      <Tooltip
        title={`Send and close (${formatShortcut(messageInputShortcuts.SEND_AND_CLOSE)})`}
      >
        <RedoButton
          hierarchy="tertiary"
          onClick={onSendAndClose}
          pending={isSending}
          size="sm"
          text="Send and close"
        />
      </Tooltip>
      {sendAction.shortcut ? (
        <Tooltip
          title={`${sendAction.title} (${formatShortcut(sendAction.shortcut)})`}
        >
          <RedoButtonDropdown
            dropdownOpen={dropdownOpen}
            hierarchy="primary"
            onClick={onSend}
            pending={isSending}
            ref={handleSetButtonRef}
            refSetToContainer
            setDropdownOpen={setDropdownOpen}
            size="sm"
            text={sendAction.title}
            theme="normal"
          >
            <RedoCommandMenu
              anchor={buttonRef}
              items={menuItems}
              open={dropdownOpen}
              placement="bottom-end"
              setOpen={setDropdownOpen}
            />
          </RedoButtonDropdown>
        </Tooltip>
      ) : (
        <RedoButtonDropdown
          dropdownOpen={dropdownOpen}
          hierarchy="primary"
          onClick={onSend}
          pending={isSending}
          ref={handleSetButtonRef}
          refSetToContainer
          setDropdownOpen={setDropdownOpen}
          size="sm"
          text={sendAction.title}
          theme="normal"
        >
          <RedoCommandMenu
            anchor={buttonRef}
            items={menuItems}
            open={dropdownOpen}
            placement="bottom-end"
            setOpen={setDropdownOpen}
          />
        </RedoButtonDropdown>
      )}
    </>
  );
});
